import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * HTTP clients for the services the admin talks to.
 *
 * The admin owns no database and holds no service secret (spec §11.6). It carries a
 * short-lived user token and calls each backend directly, which is why there is one
 * client per service rather than a single gateway.
 */

const TOKEN_KEY = 'nuat-admin-access-token';
const REFRESH_KEY = 'nuat-admin-refresh-token';
const USER_KEY = 'nuat-admin-user';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  permissions: string[];
}

export const tokens = {
  access: () => localStorage.getItem(TOKEN_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  user: (): AuthUser | null => {
    const raw = localStorage.getItem(USER_KEY);
    try {
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  },
  save(access: string, refresh: string, user: AuthUser) {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear() {
    [TOKEN_KEY, REFRESH_KEY, USER_KEY].forEach((key) => localStorage.removeItem(key));
  },
};

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: { total: number; page: number; pageSize: number; totalPages: number };
  error?: { code: string; message: string; details: unknown[] };
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details: unknown[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Single-flight refresh: many 401s at once must not trigger many refreshes. */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(authBaseUrl: string): Promise<string | null> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const refreshToken = tokens.refresh();
    if (!refreshToken) return null;
    try {
      const res = await axios.post(`${authBaseUrl}/api/v1/auth/refresh`, { refreshToken });
      const data = res.data.data as {
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      };
      tokens.save(data.accessToken, data.refreshToken, data.user);
      return data.accessToken;
    } catch {
      // The refresh token is spent, revoked or expired — the session is over.
      tokens.clear();
      return null;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

function createClient(baseUrl: string, authBaseUrl: string): AxiosInstance {
  const client = axios.create({ baseURL: `${baseUrl}/api/v1`, timeout: 20000 });

  client.interceptors.request.use((config) => {
    const token = tokens.access();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiEnvelope<unknown>>) => {
      const original = error.config as (typeof error.config & { _retried?: boolean }) | undefined;

      // One transparent refresh-and-retry per request.
      if (error.response?.status === 401 && original && !original._retried) {
        original._retried = true;
        const fresh = await refreshAccessToken(authBaseUrl);
        if (fresh) {
          original.headers = { ...original.headers, Authorization: `Bearer ${fresh}` } as never;
          return client.request(original);
        }
        window.dispatchEvent(new CustomEvent('nuat:session-expired'));
      }

      const payload = error.response?.data;
      throw new ApiError(
        payload?.error?.code ?? 'NETWORK_ERROR',
        payload?.error?.message ?? error.message ?? 'The request failed',
        error.response?.status ?? 0,
        payload?.error?.details ?? [],
      );
    },
  );

  return client;
}

const env = import.meta.env;
export const AUTH_URL = env.VITE_AUTH_API_URL ?? 'http://localhost:4001';

export const api = {
  auth: createClient(AUTH_URL, AUTH_URL),
  simulator: createClient(env.VITE_SIMULATOR_API_URL ?? 'http://localhost:4002', AUTH_URL),
  agent: createClient(env.VITE_AGENT_API_URL ?? 'http://localhost:4003', AUTH_URL),
  connector: createClient(env.VITE_CONNECTOR_API_URL ?? 'http://localhost:4004', AUTH_URL),
  ai: createClient(env.VITE_AI_API_URL ?? 'http://localhost:4005', AUTH_URL),
  notification: createClient(env.VITE_NOTIFICATION_API_URL ?? 'http://localhost:4006', AUTH_URL),
  files: createClient(env.VITE_FILE_API_URL ?? 'http://localhost:4007', AUTH_URL),
};

export const SERVICE_URLS: Record<string, string> = {
  'nuat-auth': AUTH_URL,
  'nuat-simulator-api': env.VITE_SIMULATOR_API_URL ?? 'http://localhost:4002',
  'nuat-shopping-agent': env.VITE_AGENT_API_URL ?? 'http://localhost:4003',
  'nuat-data-connector': env.VITE_CONNECTOR_API_URL ?? 'http://localhost:4004',
  'nuat-ai-api': env.VITE_AI_API_URL ?? 'http://localhost:4005',
  'nuat-notification': env.VITE_NOTIFICATION_API_URL ?? 'http://localhost:4006',
  'nuat-file-service': env.VITE_FILE_API_URL ?? 'http://localhost:4007',
};

/** Unwrap the platform envelope down to `data`. */
export async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const response = await promise;
  return response.data.data;
}

/** Unwrap a paginated response, keeping the meta block. */
export async function unwrapPaged<T>(
  promise: Promise<{ data: ApiEnvelope<T[]> }>,
): Promise<{ items: T[]; total: number; page: number; pageSize: number; totalPages: number }> {
  const response = await promise;
  return {
    items: response.data.data ?? [],
    total: response.data.meta?.total ?? 0,
    page: response.data.meta?.page ?? 1,
    pageSize: response.data.meta?.pageSize ?? 25,
    totalPages: response.data.meta?.totalPages ?? 1,
  };
}

export function money(value: number, currency = 'EGP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

export function moneyExact(value: number, currency = 'EGP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);
}

export function number(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

/** All timestamps are UTC in the database; the business reads them in Cairo (spec §29.25). */
export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', { timeZone: 'Africa/Cairo', dateStyle: 'medium', timeStyle: 'short' });
}

export function date(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', { timeZone: 'Africa/Cairo', dateStyle: 'medium' });
}
